// ./components/PaymentMethodRows.js
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Stack,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const CARD_TYPES = [
  { value: 'visa', label: 'Visa' },
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'amex', label: 'American Express' },
  { value: 'diners', label: 'Diners' },
  { value: 'isracard', label: 'ישראכרט' }
];

// Empty templates per type — every slot has `type` and `amount`; type-specific fields are filled
// only for the relevant type.
const emptyForType = (type) => ({
  type,
  amount: '',
  // Check fields
  check_number: '',
  check_bank_name: '',
  check_account_number: '',
  check_due_date: null,
  // Card fields
  card_last_four: '',
  card_type: '',
  // Transfer fields
  bank_name: '',
  bank_account_last4: '',
  transfer_date: null,
  // General
  notes: ''
});

// A slot is "empty" (eligible for auto-removal) when the amount is blank/0 AND all
// type-specific fields are also blank. Check entries and "other" entries are never
// auto-removed — the user removes them explicitly via the remove button.
const isSlotEmpty = (pm) => {
  if (pm.type === 'other') return false;
  const amt = parseFloat(pm.amount);
  const hasAmount = !Number.isNaN(amt) && amt > 0;
  if (hasAmount) return false;
  if (pm.type === 'credit_card') {
    return !pm.card_last_four && !pm.card_type;
  }
  if (pm.type === 'bank_transfer') {
    return !pm.bank_name && !pm.bank_account_last4 && !pm.transfer_date;
  }
  // Cash has no side fields
  return true;
};

const PaymentMethodRows = ({
  paymentMethods = [],
  setPaymentMethods,
  totalAmount = 0,
  disabled = false,
  compact = false,
  currencySymbol = '₪'
}) => {
  // Sections start collapsed so the dialog form isn't crowded. Auto-expanded when there's
  // already data (e.g. edit flows) so the user sees what's there. Toggled via the add button.
  const [expanded, setExpanded] = useState(paymentMethods.length > 0);

  // Extract singleton entries per type
  const cashEntry = useMemo(() => paymentMethods.find(pm => pm.type === 'cash'), [paymentMethods]);
  const cardEntry = useMemo(() => paymentMethods.find(pm => pm.type === 'credit_card'), [paymentMethods]);
  const transferEntry = useMemo(() => paymentMethods.find(pm => pm.type === 'bank_transfer'), [paymentMethods]);
  const checkEntries = useMemo(() => paymentMethods.filter(pm => pm.type === 'check'), [paymentMethods]);
  const otherEntry = useMemo(() => paymentMethods.find(pm => pm.type === 'other'), [paymentMethods]);

  // Sum of all payment methods
  const allocatedAmount = useMemo(() => {
    return paymentMethods.reduce((sum, pm) => {
      const pmAmount = parseFloat(pm.amount) || 0;
      return sum + pmAmount;
    }, 0);
  }, [paymentMethods]);

  const remainingAmount = totalAmount - allocatedAmount;
  const isBalanced = Math.abs(remainingAmount) < 0.01;

  // Upsert a singleton entry (cash / credit_card / bank_transfer). If the merged entry is
  // empty (amount blank/0 AND all side fields empty), the entry is removed from the array.
  const upsertSingleton = useCallback((type, patch) => {
    const existing = paymentMethods.find(pm => pm.type === type);
    const base = existing || emptyForType(type);
    const merged = { ...base, ...patch };

    const nextArray = paymentMethods.filter(pm => pm.type !== type);
    if (!isSlotEmpty(merged)) {
      nextArray.push(merged);
    }
    setPaymentMethods(nextArray);
  }, [paymentMethods, setPaymentMethods]);

  // Fill the current slot's amount with the remaining unallocated balance
  const fillRemaining = useCallback((type) => {
    const existing = paymentMethods.find(pm => pm.type === type);
    const currentAmt = parseFloat(existing?.amount) || 0;
    // Remaining excluding this slot's current contribution
    const remainingForSlot = totalAmount - (allocatedAmount - currentAmt);
    if (remainingForSlot > 0) {
      upsertSingleton(type, { amount: remainingForSlot.toFixed(2) });
    }
  }, [paymentMethods, totalAmount, allocatedAmount, upsertSingleton]);

  // --- Check helpers (multi-row) ---

  const addCheck = useCallback(() => {
    const newCheck = emptyForType('check');
    // Default the new check's amount to the remaining balance (if positive)
    if (remainingAmount > 0) {
      newCheck.amount = remainingAmount.toFixed(2);
    }
    setPaymentMethods([...paymentMethods, newCheck]);
  }, [paymentMethods, remainingAmount, setPaymentMethods]);

  const updateCheckAt = useCallback((checkIndex, field, value) => {
    // checkIndex is the index within checkEntries; map to absolute index in paymentMethods
    let seen = -1;
    const next = paymentMethods.map((pm) => {
      if (pm.type !== 'check') return pm;
      seen += 1;
      if (seen === checkIndex) {
        return { ...pm, [field]: value };
      }
      return pm;
    });
    setPaymentMethods(next);
  }, [paymentMethods, setPaymentMethods]);

  const removeCheckAt = useCallback((checkIndex) => {
    let seen = -1;
    const next = paymentMethods.filter((pm) => {
      if (pm.type !== 'check') return true;
      seen += 1;
      return seen !== checkIndex;
    });
    setPaymentMethods(next);
  }, [paymentMethods, setPaymentMethods]);

  // --- "Other" helpers (hidden until user opts in) ---

  const addOther = useCallback(() => {
    if (paymentMethods.some(pm => pm.type === 'other')) return;
    const entry = emptyForType('other');
    if (remainingAmount > 0) {
      entry.amount = remainingAmount.toFixed(2);
    }
    setPaymentMethods([...paymentMethods, entry]);
  }, [paymentMethods, remainingAmount, setPaymentMethods]);

  const removeOther = useCallback(() => {
    setPaymentMethods(paymentMethods.filter(pm => pm.type !== 'other'));
  }, [paymentMethods, setPaymentMethods]);

  // --- Render helpers ---

  const renderSlotHeader = (icon, label, entry, type) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
        {label}
      </Typography>
      {!disabled && remainingAmount > 0 && (!entry || !(parseFloat(entry.amount) > 0)) && (
        <Button
          size="small"
          variant="text"
          onClick={() => fillRemaining(type)}
          sx={{ minWidth: 0 }}
        >
          מלא {currencySymbol}{remainingAmount.toFixed(2)}
        </Button>
      )}
    </Box>
  );

  // Larger inputs for the fields users actually fill on every check (amount, check number)
  const checkPrimaryInputSx = {
    '& .MuiInputBase-input': { fontSize: '1.15rem', fontWeight: 500 },
  };

  const noSpinnerSx = {
    '& input': {
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
      MozAppearance: 'textfield',
    },
  };

  const slotPaperSx = {
    p: compact ? 1.5 : 2,
    backgroundColor: '#fafafa',
    borderRadius: 1,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <Box sx={{ mt: 2 }}>
        {/* Header: add-button when collapsed; title + balance chip + hide button when expanded */}
        {!expanded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setExpanded(true)}
              disabled={disabled}
              sx={{ borderStyle: 'dashed' }}
            >
              הוסף אמצעי תשלום
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              אמצעי תשלום
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={isBalanced ? `${currencySymbol}${allocatedAmount.toFixed(2)} ✓` : `${currencySymbol}${allocatedAmount.toFixed(2)} / ${currencySymbol}${totalAmount.toFixed(2)}`}
                color={isBalanced ? 'success' : remainingAmount < 0 ? 'error' : 'warning'}
                size="small"
              />
              <IconButton
                size="small"
                onClick={() => setExpanded(false)}
                disabled={disabled}
                aria-label="סגור אמצעי תשלום"
              >
                <ExpandLessIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        <Collapse in={expanded} unmountOnExit={false}>
        <Stack spacing={1.5}>
          {/* CASH slot */}
          <Paper elevation={1} sx={slotPaperSx}>
            {renderSlotHeader(<PaymentsIcon fontSize="small" />, 'מזומן', cashEntry, 'cash')}
            <TextField
              fullWidth
              size="small"
              label="סכום"
              type="number"
              value={cashEntry?.amount ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || parseFloat(val) >= 0) {
                  upsertSingleton('cash', { amount: val });
                }
              }}
              onWheel={(e) => e.target.blur()}
              onFocus={(e) => e.target.select()}
              disabled={disabled}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Paper>

          {/* CHECKS section (multi-row) */}
          <Paper elevation={1} sx={slotPaperSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}>
                <ReceiptLongIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                צ'קים
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCheck}
                disabled={disabled}
              >
                הוסף צ'ק
              </Button>
            </Box>

            {checkEntries.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                לא נוספו צ'קים
              </Typography>
            )}

            <Stack spacing={1}>
              {checkEntries.map((pm, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Primary row: amount + check number get the most room and a larger font */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr 1fr auto' },
                      gap: 1.5,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      fullWidth
                      label="סכום"
                      type="number"
                      value={pm.amount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || parseFloat(val) >= 0) {
                          updateCheckAt(index, 'amount', val);
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      onFocus={(e) => e.target.select()}
                      disabled={disabled}
                      inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                      sx={{ ...checkPrimaryInputSx, ...noSpinnerSx }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => removeCheckAt(index)}
                      disabled={disabled}
                      aria-label="מחק צ'ק"
                      sx={{ gridColumn: { xs: 2, sm: 4 }, gridRow: 1 }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                    <TextField
                      fullWidth
                      label="מס' צ'ק"
                      value={pm.check_number}
                      onChange={(e) => updateCheckAt(index, 'check_number', e.target.value)}
                      disabled={disabled}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ ...checkPrimaryInputSx, gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                    />
                    <DatePicker
                      label="תאריך פירעון"
                      value={pm.check_due_date}
                      onChange={(date) => updateCheckAt(index, 'check_due_date', date)}
                      disabled={disabled}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { direction: 'ltr', gridColumn: { xs: '1 / -1', sm: 'auto' } }
                        }
                      }}
                    />
                  </Box>

                  {/* Secondary row: optional bank details */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 1 }}>
                    פרטי בנק (לא חובה)
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="בנק"
                      value={pm.check_bank_name}
                      onChange={(e) => updateCheckAt(index, 'check_bank_name', e.target.value)}
                      disabled={disabled}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="מס' חשבון"
                      value={pm.check_account_number}
                      onChange={(e) => updateCheckAt(index, 'check_account_number', e.target.value)}
                      disabled={disabled}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* BANK TRANSFER slot */}
          <Paper elevation={1} sx={slotPaperSx}>
            {renderSlotHeader(<AccountBalanceIcon fontSize="small" />, 'העברה בנקאית', transferEntry, 'bank_transfer')}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="סכום"
                  type="number"
                  value={transferEntry?.amount ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || parseFloat(val) >= 0) {
                      upsertSingleton('bank_transfer', { amount: val });
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => e.target.select()}
                  disabled={disabled}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="בנק"
                  value={transferEntry?.bank_name ?? ''}
                  onChange={(e) => upsertSingleton('bank_transfer', { bank_name: e.target.value })}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="4 ספרות חשבון"
                  value={transferEntry?.bank_account_last4 ?? ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    upsertSingleton('bank_transfer', { bank_account_last4: val });
                  }}
                  disabled={disabled}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <DatePicker
                  label="תאריך העברה"
                  value={transferEntry?.transfer_date ?? null}
                  onChange={(date) => upsertSingleton('bank_transfer', { transfer_date: date })}
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: { direction: 'ltr' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* CREDIT CARD slot */}
          <Paper elevation={1} sx={slotPaperSx}>
            {renderSlotHeader(<CreditCardIcon fontSize="small" />, 'כרטיס אשראי', cardEntry, 'credit_card')}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="סכום"
                  type="number"
                  value={cardEntry?.amount ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || parseFloat(val) >= 0) {
                      upsertSingleton('credit_card', { amount: val });
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => e.target.select()}
                  disabled={disabled}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="4 ספרות אחרונות"
                  value={cardEntry?.card_last_four ?? ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    upsertSingleton('credit_card', { card_last_four: val });
                  }}
                  disabled={disabled}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="סוג כרטיס"
                  value={cardEntry?.card_type ?? ''}
                  onChange={(e) => upsertSingleton('credit_card', { card_type: e.target.value })}
                  disabled={disabled}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {CARD_TYPES.map((ct) => (
                    <MenuItem key={ct.value} value={ct.value}>
                      {ct.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* "Other" slot — hidden behind a button until the user opts in */}
          {otherEntry ? (
            <Paper elevation={1} sx={slotPaperSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                  אמצעי תשלום נוסף
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={removeOther}
                  disabled={disabled}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="סכום"
                    type="number"
                    value={otherEntry.amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || parseFloat(val) >= 0) {
                        upsertSingleton('other', { amount: val });
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    onFocus={(e) => e.target.select()}
                    disabled={disabled}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="פרטים"
                    value={otherEntry.notes}
                    onChange={(e) => upsertSingleton('other', { notes: e.target.value })}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<AddIcon />}
                onClick={addOther}
                disabled={disabled}
              >
                אמצעי תשלום נוסף
              </Button>
            </Box>
          )}
        </Stack>
        </Collapse>

        {/* Warning if not balanced */}
        {expanded && !isBalanced && allocatedAmount > 0 && (
          <Typography
            variant="caption"
            color={remainingAmount < 0 ? 'error.main' : 'warning.main'}
            sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
          >
            {remainingAmount > 0
              ? `נותר להקצות: ${currencySymbol}${remainingAmount.toFixed(2)}`
              : `חריגה: ${currencySymbol}${Math.abs(remainingAmount).toFixed(2)}`}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default PaymentMethodRows;
